import { cn } from 'lib/utils';
import { CheckIcon } from 'lucide-react';
import * as React from 'react';

export type StepperStep = {
  label: React.ReactNode;
  /**
   * `done` is ticked, `current` is ringed, `upcoming` is a thin ring; a plain
   * explanation of the steps (no progress to show) leaves it out.
   */
  state?: 'done' | 'current' | 'upcoming';
};

/** The line from a step to the next: solid once it is done, dotted while it is under way. */
function StepperConnector({ state }: { state: StepperStep['state'] }) {
  if (state === 'current') {
    return (
      <span aria-hidden className="flex h-5 flex-col gap-0.5">
        <span className="h-1.25 w-0.5 bg-primary" />
        <span className="h-1.75 w-0.5 bg-primary" />
        <span className="h-1 w-0.5 bg-primary" />
      </span>
    );
  }
  return <span aria-hidden className={cn('h-5 w-0.5', state ? 'bg-primary' : 'bg-border')} />;
}

/**
 * A vertical stepper: numbered steps joined by a line, either explaining a
 * process (no `state`) or showing how far it has got (Figma "Comment ça
 * marche", "Et maintenant" in the messaging redesign).
 */
function Stepper({ steps, className, ...props }: { steps: StepperStep[] } & React.ComponentProps<'ol'>) {
  return (
    <ol data-slot="stepper" className={cn('flex flex-col', className)} {...props}>
      {steps.map((step, index) => (
        <li key={index} data-slot="stepper-step" data-state={step.state} className="flex gap-3">
          <div className="flex flex-col items-center">
            <span
              className={cn(
                'flex size-8 shrink-0 items-center justify-center rounded-full text-xs font-medium text-foreground',
                !step.state && 'bg-border',
                step.state === 'done' && 'bg-primary',
                step.state === 'current' && 'border-2 border-inverse bg-card font-bold',
                step.state === 'upcoming' && 'border border-info-border text-secondary-foreground',
              )}
            >
              {step.state === 'done' ? <CheckIcon aria-hidden className="size-3.5" /> : index + 1}
            </span>
            {index < steps.length - 1 && <StepperConnector state={step.state} />}
          </div>
          {/* Level with its number, not centred on the row (which also holds the line to the next step) */}
          <span
            className={cn(
              'flex min-h-8 items-center self-start text-sm text-foreground',
              step.state === 'done' && 'font-bold',
              step.state === 'current' && 'font-medium',
              step.state === 'upcoming' && 'text-secondary-foreground',
            )}
          >
            {step.label}
          </span>
        </li>
      ))}
    </ol>
  );
}

export { Stepper };
